/**
 * @file coupons.ts
 *
 * Coupon API for the shop admin, wired to the real commerce-engine `/v1` coupons
 * API. Reads ({@link fetchCoupons}, {@link fetchCouponById}) map the `/v1` response
 * to the design-reviewed {@link ShopCoupon} via {@link toShopCoupon}; writes
 * ({@link createCoupon}, {@link updateCoupon}) map the form payload to the `/v1`
 * request via {@link buildCouponDto} (one builder for POST + partial PATCH). This
 * module is the only place that knows the backend's specifics, so the store and
 * screens stay untouched.
 */

import { v1Get, v1Mutate } from './v1-request';
import { minorUnitsToDecimal, parseDecimal } from './money';
import type { WcResult } from './types';
import type {
  FetchCouponsParams,
  ShopCoupon,
  ShopCouponPayload,
} from './shop-types';
import type {
  CouponDiscountTypeV1,
  CouponResponseV1,
  CreateCouponRequestV1,
} from './v1-types';

/** Default coupons per page for the list view. */
export const COUPONS_PER_PAGE = 20;

/**
 * Formats a `/v1` coupon amount for display. A `percent` amount is basis points
 * (1000 → `"10"`, 525 → `"5.25"`); a `fixed_*` amount is minor units (1000 →
 * `"10.00"`). Both divide by 100 but format per type.
 *
 * @param amount - The raw `/v1` amount (basis points or minor units).
 * @param type - The discount type, selecting the formatting.
 *
 * @returns The display amount string.
 */
function couponAmountToDisplay(amount: number, type: CouponDiscountTypeV1): string {
  return type === 'percent' ? String(amount / 100) : minorUnitsToDecimal(amount);
}

/**
 * Maps a `/v1` coupon resource to the {@link ShopCoupon} the screens consume:
 * snake-case field renames, the per-type amount formatting
 * ({@link couponAmountToDisplay}), and the minor-unit money fields as decimal
 * strings ({@link minorUnitsToDecimal}; `""` when null).
 *
 * @param c - The `/v1` coupon resource (admin audience).
 *
 * @returns The coupon in the design-reviewed `Shop*` shape.
 */
export function toShopCoupon(c: CouponResponseV1): ShopCoupon {
  return {
    id: c.id,
    code: c.code,
    discount_type: c.discountType,
    amount: couponAmountToDisplay(c.amount, c.discountType),
    date_created: c.createdAt,
    date_expires: c.dateExpires,
    usage_count: c.usageCount,
    usage_limit: c.usageLimit,
    usage_limit_per_user: c.usageLimitPerUser,
    description: c.description ?? '',
    individual_use: c.individualUseOnly,
    free_shipping: c.grantsFreeShipping,
    minimum_amount: c.minimumAmount != null ? minorUnitsToDecimal(c.minimumAmount) : '',
    maximum_amount: c.maximumAmount != null ? minorUnitsToDecimal(c.maximumAmount) : '',
  };
}

/** Converts an optional decimal money string to minor units, or `null` when blank. */
function toMinorUnits(value?: string): number | null {
  const parsed = value != null && value.trim() !== '' ? parseDecimal(value) : null;
  return parsed === null ? null : Math.round(parsed * 100);
}

/**
 * Builds the `/v1` coupon write body from the form's payload. One builder serves
 * both `POST` and the partial `PATCH` (the form is a full editor): `code` /
 * `discountType` / `amount` and the two toggles are always sent, and the editable
 * optionals are sent as `null` when blank so clearing a field persists on `PATCH`
 * (and `null` is the contract default on `POST`). `amount` becomes `decimal * 100`,
 * which is correct for both percent (10 → 1000 basis points) and fixed (10.00 →
 * 1000 minor units). The expiry is stamped to an end-of-day ISO datetime (the
 * contract's `z.iso.datetime()` requires a timezone). Fields the form never edits
 * are omitted (POST takes the contract defaults; PATCH leaves them unchanged).
 *
 * @param payload - The coupon write payload from the form.
 *
 * @returns The `/v1` coupon write body.
 */
export function buildCouponDto(payload: ShopCouponPayload): CreateCouponRequestV1 {
  return {
    code: payload.code,
    discountType: payload.discount_type as CouponDiscountTypeV1,
    amount: Math.round((parseDecimal(payload.amount) ?? 0) * 100),
    description: payload.description || null,
    grantsFreeShipping: payload.free_shipping,
    individualUseOnly: payload.individual_use,
    minimumAmount: toMinorUnits(payload.minimum_amount),
    maximumAmount: toMinorUnits(payload.maximum_amount),
    usageLimit: payload.usage_limit ? payload.usage_limit : null,
    dateExpires: payload.date_expires ? `${payload.date_expires}T23:59:59.000Z` : null,
  };
}

/**
 * Fetches a page of coupons with an optional search term.
 *
 * `search` is omitted when empty so the query string only carries active filters,
 * mirroring the products/categories APIs.
 *
 * @param params - Pagination and search options.
 *
 * @returns The coupons page wrapped in a {@link WcResult} (with totals).
 *
 * @throws {WcApiError} When the request fails.
 */
export async function fetchCoupons(
  params: FetchCouponsParams = {},
): Promise<WcResult<ShopCoupon[]>> {
  const { page = 1, perPage = COUPONS_PER_PAGE, search = '' } = params;
  const qs = new URLSearchParams({ page: String(page), pageSize: String(perPage) });
  if (search) {
    qs.set('search', search);
  }
  const res = await v1Get<CouponResponseV1[]>(`/coupons?${qs.toString()}`);
  return {
    data: res.data.map(toShopCoupon),
    total: res.total,
    totalPages: res.totalPages,
  };
}

/**
 * Fetches a single coupon by id, used to seed the edit form on a direct link or
 * refresh (the list view passes its cached copy when present).
 *
 * @param id - The coupon id to fetch.
 *
 * @returns The coupon.
 *
 * @throws {WcApiError} When the request fails (e.g. 404 for an unknown id).
 */
export async function fetchCouponById(id: string): Promise<ShopCoupon> {
  const res = await v1Get<CouponResponseV1>(`/coupons/${id}`);
  return toShopCoupon(res.data);
}

/**
 * Creates a new coupon against the `/v1` API.
 *
 * @param payload - The coupon write payload.
 *
 * @returns The created coupon, mapped to the `Shop*` shape.
 *
 * @throws {WcApiError} When the request fails (e.g. a `coupon_code_conflict` 409).
 */
export async function createCoupon(
  payload: ShopCouponPayload,
): Promise<ShopCoupon> {
  const created = await v1Mutate<CouponResponseV1>('/coupons', 'POST', buildCouponDto(payload));
  return toShopCoupon(created);
}

/**
 * Updates an existing coupon against the `/v1` API (partial `PATCH`).
 *
 * @param id - The coupon id to update.
 * @param payload - The coupon write payload.
 *
 * @returns The updated coupon, mapped to the `Shop*` shape.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function updateCoupon(
  id: string,
  payload: ShopCouponPayload,
): Promise<ShopCoupon> {
  const updated = await v1Mutate<CouponResponseV1>(`/coupons/${id}`, 'PATCH', buildCouponDto(payload));
  return toShopCoupon(updated);
}

/**
 * Soft-deletes a coupon (`DELETE /v1/coupons/:id`, returning 204).
 *
 * @param id - The coupon id to delete.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function deleteCoupon(id: string): Promise<void> {
  await v1Mutate(`/coupons/${id}`, 'DELETE');
}
